// AuditLogPanel — Jun 2026 (security #5).
// Read-only feed of admin write-actions auto-captured by the backend
// middleware. Source: GET /api/admin/audit-log
import { useEffect, useState } from "react";
import { ClipboardText, ArrowsClockwise, UserSwitch, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const METHOD_COLOR = { POST: "#2F6B3A", PUT: "#B8860B", PATCH: "#B8860B", DELETE: "#C16E5A" };

function ago(iso) {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.floor(s)}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}

export default function AuditLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit-log", { params: { limit: 60 } });
      setRows(r.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Clear the entire admin activity log? This frees space and cannot be undone.")) return;
    try {
      const r = await api.delete("/admin/audit-log");
      toast.success(`Cleared ${r.data?.cleared ?? 0} log entries.`);
      setRows([]);
    } catch { toast.error("Couldn't clear the log."); }
  };

  return (
    <div className="bg-white border hairline rounded-xl p-5 shadow-sm" data-testid="audit-log-panel">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardText size={18} weight="duotone" className="text-[#4A5D4E]" />
        <div className="font-serif-display text-lg flex-1">Admin activity log</div>
        <button onClick={clearAll} data-testid="audit-clear-all"
          title="Clear all log entries"
          className="text-[#C16E5A] hover:text-[#7A2A20] inline-flex items-center gap-1 text-xs">
          <Trash size={14} /> Clear all
        </button>
        <button onClick={reload} disabled={loading} data-testid="audit-refresh"
          className="text-[#6B6A68] hover:text-[#1C1B1A] disabled:opacity-40">
          <ArrowsClockwise size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      <p className="text-xs text-[#6B6A68] mb-4">Every admin change (who, what, when) — captured automatically.</p>

      {loading && rows.length === 0 && <div className="text-sm text-[#6B6A68]">Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-sm text-[#6B6A68]">No admin actions recorded yet.</div>}

      {rows.length > 0 && (
        <div className="max-h-80 overflow-y-auto -mx-1">
          <table className="w-full text-xs">
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b hairline last:border-0" data-testid="audit-row">
                  <td className="py-1.5 px-1 whitespace-nowrap text-[#9A9892]">{ago(r.at)}</td>
                  <td className="py-1.5 px-1 text-[#1C1B1A] truncate max-w-[180px]">
                    {r.actor}
                    {r.impersonator_id && <UserSwitch size={11} className="inline ml-1 text-[#C16E5A]" title="via impersonation" />}
                  </td>
                  <td className="py-1.5 px-1 whitespace-nowrap font-mono" style={{ color: METHOD_COLOR[r.method] || "#6B6A68" }}>{r.method}</td>
                  <td className="py-1.5 px-1 text-[#3A3937] font-mono truncate max-w-[260px]">{r.path?.replace("/api/admin", "")}</td>
                  <td className="py-1.5 px-1 text-right tabular-nums text-[#6B6A68]">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
